import type { TFunction } from 'i18next';

import type { ApiError } from '@/types/api';

/** Axios fallback when the body has no parseable user message. */
const GENERIC_AXIOS_STATUS_RE = /^Request failed with status code \d{3}$/i;

/**
 * True when `message` is Axios's generic English line (not from API JSON).
 */
export function isGenericAxiosFailedMessage(message: string): boolean {
  return GENERIC_AXIOS_STATUS_RE.test(message.trim());
}

export function getHttpStatusFromApiError(error: unknown): number | undefined {
  if (!error || typeof error !== 'object') return undefined;
  const s = (error as ApiError).status;
  return typeof s === 'number' && Number.isFinite(s) ? s : undefined;
}

/**
 * Parse a JSON error body (message, detail, error, title, FastAPI validation array).
 */
export function extractAxiosResponseDataMessage(data: unknown, fallback: string): string {
  if (data == null || typeof data !== 'object') return fallback;
  const d = data as Record<string, unknown>;
  const pick = (v: unknown) => (typeof v === 'string' && v.trim() ? v.trim() : null);
  const fromMessage = pick(d.message);
  if (fromMessage) return fromMessage;
  const fromDetail = pick(d.detail);
  if (fromDetail) return fromDetail;
  if (Array.isArray(d.detail) && d.detail.length > 0) {
    const first = d.detail[0];
    if (first && typeof first === 'object' && first !== null && 'msg' in first) {
      const msg = (first as { msg?: unknown }).msg;
      const s = pick(msg);
      if (s) return s;
    }
  }
  const fromError = pick(d.error);
  if (fromError) return fromError;
  const fromTitle = pick(d.title);
  if (fromTitle) return fromTitle;
  return fallback;
}

/**
 * User-facing string from `.unwrap()` rejection (ApiError from axios base query) or a thrown Error.
 */
export function getApiErrorMessage(error: unknown, fallback: string): string {
  if (error && typeof error === 'object') {
    const e = error as ApiError & { message?: string };
    if (typeof e.message === 'string' && e.message.trim()) return e.message.trim();
    if (e.errors && typeof e.errors === 'object') {
      const first = Object.values(e.errors)[0];
      if (Array.isArray(first) && first[0]) return String(first[0]);
    }
  }
  if (error instanceof Error && error.message.trim()) return error.message.trim();
  return fallback;
}

/**
 * Maps RTK/Axios errors to copy suitable for toasts: network vs backend (HTTP) vs body message.
 *
 * - No HTTP response → `errors.networkError`
 * - Generic Axios status line or empty message → `errors.serverError` (5xx) or `errors.somethingWentWrong` (other)
 * - Otherwise prefers the API/body `message` (validation, business rules).
 */
export function formatUserFacingApiError(error: unknown, t: TFunction): string {
  const e = error as ApiError;
  if (e?.noResponse) return t('errors.networkError');

  const status = e?.status ?? 0;
  const msg = getApiErrorMessage(error, '');

  if (!msg || isGenericAxiosFailedMessage(msg)) {
    if (status >= 500) return t('errors.serverError');
    if (status >= 400) return t('errors.somethingWentWrong');
    return t('errors.somethingWentWrong');
  }

  return msg;
}
